import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../data/mock';

const ServiceDetail = () => {
  const { id } = useParams();
  const service = services.find(s => s.id === parseInt(id));

  if (!service) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Service not found</h2>
        <Link to="/services">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            Back to Services
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      {/* Hero */}
      <section className="relative h-96 overflow-hidden">
        <img 
          src={service.image} 
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-12">
            <Link to="/services" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors">
              <ArrowLeft size={20} />
              <span>Back to Services</span>
            </Link>
            <h1 className="text-5xl font-bold text-white mb-4">{service.title}</h1>
            <p className="text-xl text-white/90 max-w-3xl">{service.description}</p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-lg text-gray-600 leading-relaxed">{service.details.overview}</p>
            </div>

            {/* Our Approach */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Approach</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {service.details.approaches.map((approach, index) => (
                  <Card key={index} className="border-l-4 border-l-blue-600 shadow-sm">
                    <CardContent className="p-4 flex items-start gap-3">
                      <CheckCircle className="text-blue-600 mt-1 flex-shrink-0" size={20} />
                      <p className="text-gray-700">{approach}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Conditions */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Conditions We Treat</h2>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8">
                <div className="flex flex-wrap gap-3">
                  {service.details.conditions.map((condition, index) => (
                    <span 
                      key={index}
                      className="bg-white px-4 py-2 rounded-full text-gray-700 shadow-sm border border-blue-200"
                    >
                      {condition}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <Card className="bg-gradient-to-r from-blue-600 to-blue-500 text-white border-none shadow-2xl">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Discuss Your Care Options?</h3>
                <p className="text-blue-50 mb-6">
                  Our compassionate team is here to answer your questions and guide you through the process.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-full">
                      Book Consultation
                    </Button>
                  </Link>
                  <a href="tel:9123647588">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full">
                      <Phone size={18} className="mr-2" />
                      Call Now
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
